import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import Api from 'app/helpers/api';
import App from 'app/helpers/app';
import Me from 'app/helpers/me';
import { take } from 'rxjs/operators';
import { CityService } from 'app/shared/services/cidades.service';

@Component({
  selector: 'app-municipios-conferidor',
  templateUrl: './municipios-conferidor.component.html',
  styleUrls: ['./municipios-conferidor.component.scss']
})
export class MunicipiosConferidorComponent implements OnInit {
  apiCall = this.api.new().silent();
  
  // Variáveis para paginação das cidades
  cidadesPage = 1;
  cidadesPageSize = 10;
  cidadesTotal = 0;
  cidadesData = [];
  
  // Filtro de pesquisa
  filterMunicipio: string = '';
  
  // Propriedades para ordenação
  currentSortColumn: string = '';
  currentSortDirection: string = 'asc';
  
  // Referência ao Math para uso no template
  Math = Math;

  constructor(
    private router: Router,
    private spinner: NgxSpinnerService,
    private me: Me,
    private app: App,
    private http: HttpClient,
    private api: Api,
    private cityService: CityService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  async ngOnInit() {
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });
    
    try {
      await this.loadCidades();
    } finally {
      this.spinner.hide();
    }
  }

  async loadCidades() {
    const params = this.buildSearchParams();
    
    try {
      const cities = await this.cityService.getCidades(params);
      
      // Garantir que a resposta seja parseada corretamente
      const responseData = cities ? JSON.parse(JSON.stringify(cities)) : null;
      
      if(responseData && responseData.data){
        // Criar nova referência do array para forçar detecção de mudanças
        this.cidadesData = [...responseData.data];
        this.cidadesTotal = responseData.total || 0;
        console.log('Cidades carregadas:', this.cidadesData.length, 'de', this.cidadesTotal);
      } else {
        // Se não houver dados, garantir que o array está vazio
        this.cidadesData = [];
        this.cidadesTotal = 0;
      }
      
      // Forçar detecção de mudanças após atualizar os dados
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Erro ao carregar cidades:', error);
      this.cidadesData = [];
      this.cidadesTotal = 0;
      this.cdr.detectChanges();
      await this.app.alert('Erro!', 'Erro ao carregar municípios. Tente novamente.', 'error');
    }
  }

  // Método para buscar dados com indicador de carregamento
  async loadCidadesWithCurrentFilters() {
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });

    try {
      await this.loadCidades();
    } finally {
      this.spinner.hide();
    }
  }

  // Mudança de página
  async onCidadesPageChange(page: number) {
    this.cidadesPage = page;
    await this.loadCidadesWithCurrentFilters();
  }

  // Construir parâmetros de busca
  buildSearchParams(): any {
    const offset = (this.cidadesPage - 1) * this.cidadesPageSize;
    let params: any = {
      offset: offset,
      limit: this.cidadesPageSize
    };

    // Filtro por nome do município
    if (this.filterMunicipio && this.filterMunicipio.trim() !== '') {
      params.nome = this.filterMunicipio.trim();
    }

    // Ordenação
    if (this.currentSortColumn && this.currentSortColumn.trim() !== '') {
      params.order_by = this.currentSortColumn;
      params.order_direction = this.currentSortDirection.toUpperCase();
    }

    return params;
  }

  // Método para ordenação das colunas
  sortTable(column: string) {
    const columnMap = {
      'nome': 'nome',
      'usuarios': 'quantidade_usuarios',
      'orgaos': 'quantidade_orgaos',
      'status': 'status'
    };

    const mappedColumn = columnMap[column] || column;

    if (this.currentSortColumn === mappedColumn) {
      this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = mappedColumn;
      this.currentSortDirection = 'asc';
    }

    this.cidadesPage = 1;
    this.loadCidadesWithCurrentFilters();
  }

  // Método para mostrar indicador de ordenação
  getSortIcon(column: string): string {
    const columnMap = {
      'nome': 'nome',
      'usuarios': 'quantidade_usuarios',
      'orgaos': 'quantidade_orgaos',
      'status': 'status'
    };

    if (this.currentSortColumn === columnMap[column]) {
      return this.currentSortDirection === 'asc' ? 'fa-caret-up' : 'fa-caret-down';
    }
    return '';
  }

  // Método para verificar se a coluna está sendo ordenada
  isColumnSorted(column: string): boolean {
    const columnMap = {
      'nome': 'nome',
      'usuarios': 'quantidade_usuarios',
      'orgaos': 'quantidade_orgaos',
      'status': 'status'
    };
    return this.currentSortColumn === columnMap[column];
  }

  getStatusCidade(cidade: any): string {
    const temUsuarios = cidade.quantidade_usuarios > 0;
    const temOrgaos = cidade.quantidade_orgaos > 0;
    
    if (temUsuarios && temOrgaos) {
      return 'Ativo';
    } else if (temUsuarios || temOrgaos) {
      return 'Incompleto';
    } else {
      return 'Inativo';
    }
  }

  getStatusClass(cidade: any): string {
    const status = this.getStatusCidade(cidade);
    switch (status) {
      case 'Ativo':
        return 'status-ativo';
      case 'Incompleto':
        return 'status-incompleto';
      case 'Inativo':
        return 'status-inativo';
      default:
        return 'status-inativo';
    }
  }

  // Aplicar filtros
  applyFilters() {
    this.cidadesPage = 1; // Reset para primeira página
    this.loadCidadesWithCurrentFilters();
  }

  // Limpar filtros
  clearFilters() {
    this.filterMunicipio = '';
    this.cidadesPage = 1;
    this.currentSortColumn = '';
    this.currentSortDirection = 'asc';
    this.loadCidadesWithCurrentFilters();
  }

  // Navegar para a tela de servidores do município
  goToServidores(cidadeId: number) {
    this.router.navigate(['/page/conferidor/servidores'], { queryParams: { idCidade: cidadeId } });
  }
}
